import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Combobox } from '@Components/Combobox';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import ShowFor from '@Components/ShowFor';
import { useFishV2 } from '@Hooks/useFishV2';
import { useSearchFieldValues } from '@Hooks/useOmniSearch';
import service from '@Service';
import type { FishV2, MemberStatus } from '@Types/fish';
import { formatPhoneNumber } from '@Utils/utils';
import type { ColumnFiltersState } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GREEN, ORANGE, RED } from '../../utils';

const NOT_AVAILABLE = '-';
const SEARCH_DEBOUNCE_MS = 400;

const STATUS_OPTIONS = [
  { value: 'active,cancel_pending', label: 'Current Members' },
  { value: 'active', label: 'Active' },
  { value: 'cancel_pending', label: 'Cancel Pending' },
  { value: 'cancel_expired', label: 'Cancel Expired' },
  { value: 'active,cancel_pending,cancel_expired', label: 'All Members' }
];
const DEFAULT_STATUS = STATUS_OPTIONS[0].value;

const STATUS_BADGES: Record<MemberStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: GREEN },
  cancel_pending: { label: 'Cancel Pending', color: ORANGE },
  cancel_expired: { label: 'Cancel Expired', color: RED }
};

// Filter bar values arrive from the ui-kit editor: dates as UTC millisecond bounds, money as numbers.
const DATE_COLUMNS = new Set([
  'member.registrationDate',
  'member.nextRenewalDate',
  'member.nextWalletRenewalDate',
  'member.cancelledAt'
]);
const toSeconds = (ms: string) => Math.floor(Number(ms) / 1000);

const toFilterValue = (id: string, operator: string, value: string): string => {
  if (DATE_COLUMNS.has(id)) {
    if (operator === 'before') return `<${toSeconds(value)}`;
    if (operator === 'after') return `>${toSeconds(value)}`;
    const [start, end] = value.split('..');
    return `${toSeconds(start)}..${toSeconds(end)}`;
  }
  if (operator === 'greaterThan') return `>${value}`;
  if (operator === 'lessThan') return `<${value}`;
  return value;
};

/** Filter-bar state → `GET /v2/fish` filter params. */
const buildMemberFilterParams = (filters: ColumnFiltersState) =>
  Object.fromEntries(
    filters.flatMap((filter) => {
      const { operator, value } = (filter.value ?? {}) as { operator?: string; value?: unknown };
      if (!operator || value === undefined || value === '') return [];
      return [[`filter[${filter.id}]`, toFilterValue(filter.id, operator, String(value))]];
    })
  );

const formatDate = (ms: number | null) => (ms ? DateTime.fromMillis(ms).toFormat('MMM dd, yyyy') : NOT_AVAILABLE);
const toMillis = (iso: string | null | undefined) => (iso ? DateTime.fromISO(iso).toMillis() : null);
const formatMoney = (value: number | null) =>
  typeof value === 'number' ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : NOT_AVAILABLE;

const memberColumnConfigs: ColumnConfig<FishV2>[] = [
  {
    id: 'member.firstName',
    headerLabel: 'First Name',
    accessor: (row) => row.member?.firstName || NOT_AVAILABLE,
    type: 'text',
    size: 120,
    enableFiltering: false
  },
  {
    id: 'member.lastName',
    headerLabel: 'Last Name',
    accessor: (row) => row.member?.lastName || NOT_AVAILABLE,
    type: 'text',
    size: 120,
    enableFiltering: false
  },
  {
    id: 'email',
    headerLabel: 'Email',
    accessor: (row) => row.email || NOT_AVAILABLE,
    type: 'text'
  },
  {
    id: 'phoneNumber',
    headerLabel: 'Phone Number',
    accessor: (row) => row.phoneNumber,
    type: 'text',
    cell: (info) => (info.getValue() ? formatPhoneNumber(info.getValue()) : NOT_AVAILABLE),
    enableSorting: false,
    enableFiltering: false
  },
  {
    id: 'member.subscriptionTier',
    headerLabel: 'Subscription Tier',
    accessor: (row) =>
      row.member?.subscriptionTier
        ? `${row.member.isShared ? '[Shared] ' : ''}${row.member.subscriptionTier}`
        : NOT_AVAILABLE,
    type: 'text',
    enableFiltering: false
  },
  {
    id: 'member.inviteCode',
    headerLabel: 'Invite Code',
    accessor: (row) => row.member?.inviteCode || NOT_AVAILABLE,
    type: 'text'
  },
  {
    id: 'mtdSpend',
    headerLabel: 'MTD Spend',
    accessor: (row) => row.mtdSpend ?? null,
    type: 'currency',
    cell: (info) => formatMoney(info.getValue()),
    meta: { align: 'right' }
  },
  {
    id: 'ytdSpend',
    headerLabel: 'YTD Spend',
    accessor: (row) => row.ytdSpend ?? null,
    type: 'currency',
    cell: (info) => formatMoney(info.getValue()),
    meta: { align: 'right' }
  },
  {
    id: 'member.registrationDate',
    headerLabel: 'Registration Date',
    accessor: (row) => toMillis(row.member?.registrationDate),
    type: 'date',
    cell: (info) => formatDate(info.getValue())
  },
  {
    id: 'member.nextRenewalDate',
    headerLabel: 'Next Renewal Date',
    accessor: (row) => toMillis(row.member?.nextRenewalDate),
    type: 'date',
    cell: (info) => formatDate(info.getValue())
  },
  {
    id: 'member.nextWalletRenewalDate',
    headerLabel: 'Next Wallet Recharge',
    accessor: (row) => toMillis(row.member?.nextWalletRenewalDate),
    type: 'date',
    cell: (info) => formatDate(info.getValue())
  },
  {
    id: 'member.cancelledAt',
    headerLabel: 'Cancellation Date',
    accessor: (row) => toMillis(row.member?.cancelledAt),
    type: 'date',
    cell: (info) => formatDate(info.getValue())
  },
  {
    id: 'member.status',
    headerLabel: 'Status',
    accessor: (row) => row.member?.status ?? null,
    type: 'text',
    // Filtered by the status dropdown, which owns filter[member.status].
    enableFiltering: false,
    cell: (info) => {
      const badge = STATUS_BADGES[info.getValue() as MemberStatus];
      return badge ? (
        <Badge className="text-black" style={{ backgroundColor: badge.color }}>
          {badge.label}
        </Badge>
      ) : (
        NOT_AVAILABLE
      );
    }
  }
];

const ALL_TIERS = '';

/** The members table, read from `GET /v2/fish` (the `fish` search index). */
export default function MembersList() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [tier, setTier] = useState(ALL_TIERS);
  const [exporting, setExporting] = useState(false);

  const baseSearchParams = useCallback(
    () => ({
      'filter[member.status]': status,
      ...(search ? { q: search } : {}),
      ...(tier ? { 'filter[member.subscriptionTier]': tier } : {})
    }),
    [search, status, tier]
  );

  const controller = useResourceTableController<FishV2>({
    columnsConfig: memberColumnConfigs,
    defaultSort: { id: 'member.registrationDate', desc: true },
    baseSearchParams,
    buildFilterParams: buildMemberFilterParams,
    initialPageSize: 10,
    initialPinning: { left: ['member.firstName', 'member.lastName'], right: [] },
    // Meilisearch filters match whole values only; partial matches go through the search box.
    filterOperators: { email: ['equals'], 'member.inviteCode': ['equals'] }
  });

  // Reset to the first page only on a real change, so a deep-linked ?page= survives load.
  const { setPageIndex } = controller;
  useEffect(() => {
    const next = searchInput.trim();
    if (next === search) return;
    const handle = setTimeout(() => {
      setSearch(next);
      setPageIndex(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput, search, setPageIndex]);

  const { data: payload, status: queryStatus } = useFishV2(controller.searchParams);
  const { data: tierValues, isLoading: tiersLoading } = useSearchFieldValues('fish', 'member.subscriptionTier');

  const tierItems = useMemo(
    () => [{ value: ALL_TIERS, label: 'All tiers' }, ...(tierValues?.data ?? [])],
    [tierValues]
  );

  const exportParams = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(controller.searchParams).filter(([key]) => !key.startsWith('page['))
      ),
    [controller.searchParams]
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      await service.exportFishV2(exportParams);
    } catch (error) {
      console.error(error);
      toastError('Could not export member data.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <Input
          placeholder="Search name, email, phone or invite code..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-80"
        />
        <Combobox
          id="memberTierFilter"
          items={tierItems}
          value={tier}
          onChange={(value) => {
            setTier(value);
            setPageIndex(0);
          }}
          placeholder="All tiers"
          loading={tiersLoading}
          triggerClassName="w-[260px]"
        />
        <Combobox
          id="memberStatusFilter"
          items={STATUS_OPTIONS}
          value={status}
          onChange={(value) => {
            setStatus(value || DEFAULT_STATUS);
            setPageIndex(0);
          }}
          placeholder={STATUS_OPTIONS[0].label}
          triggerClassName="w-[280px]"
        />
      </div>

      <ResourceTableView<FishV2>
        label="Members"
        summaryLabel="Members: "
        controller={controller}
        data={payload?.data ?? []}
        meta={payload?.meta}
        status={queryStatus}
        onRowClick={(row) => window.open(`/members/${row.id}`, '_blank', 'noopener,noreferrer')}
        renderExtraActions={() => (
          <ShowFor permissions={['MEMBERS_WRITE']}>
            <Button onClick={handleExport} disabled={exporting}>
              {exporting ? 'Exporting...' : 'Export Member Data'}
            </Button>
          </ShowFor>
        )}
      />
    </div>
  );
}
